import { useState } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
  Button,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
// components
import {
  Log,
  sendDataWithGet,
  sendDataWithPost,
} from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter, isEmptyFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";
import { isEmpty } from "lodash";

import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import SearchPrompt from "../../components/SearchPrompt";
import { CustomTableCell } from "../../components/TableCell";
import Pagination from "../../components/Pagination";
import { Add, Edit } from "@mui/icons-material";
import { styles } from "../../utils/styles";
import CreateBankAccountDialog from "./Dialogs/CreateDialog";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import DeleteBankAccountDialog from "./Dialogs/DeleteDialog";
import Label from "../../components/Label";

// ----------------------------------------------------------------------

const defaultValue = { is_active: true };

const TABLE_HEAD = [
  { id: "account_number", label: "Account Number" },
  { id: "account_name", label: "Account Name" },
  { id: "bank", label: "Bank Name" },
  { id: "bank_address", label: "Bank Address" },
  { id: "bic_swift_code", label: "BIC/SWIFT Code" },
  { id: "iban", label: "IBAN" },
  { id: "is_active", label: "Active" },
  { id: "action" },
];

export default function AllBankAccount({ userRole }) {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState({});

  const [fLoaded, setFLoaded] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filterCondition, setFilterCondition] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  // the list is only queried once the user has searched, so the tab opens fast
  const [searched, setSearched] = useState(false);
  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryPage(
      {
        page: newPage + 1,
        page_size: pagination.page_size,
      },
      filter
    );
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage(
      {
        page: 1,
        page_size: event.target.value,
      },
      filter
    );
  };

  const renderData = (res) => {
    const newData = res.results.map((item) => ({
      ...item,
      merchant_name: item.merchant?.name,
    }));

    setTableData({
      ...res,
      results: newData,
    });
  };

  const queryPage = (params, filter) => {
    loaded && setLoaded(false);
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/bank_account/bank_account/filter/?${queryParams}`;
    sendDataWithPost(url, filter, renderData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const getFilterCondition = () => {
    const url = `/bank_account/bank_account/filter_conditions/`;
    sendDataWithGet(url, setFilterCondition, setFLoaded);
  };

  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the tab used to
    // load with on first render
    const fParams = isEmptyFilter(params) ? { ...defaultValue } : params;
    const newPrams = {
      ...fParams,
      merchant_id: fParams?.merchant?.id,
      account_number: fParams?.account_number?.trim(),
      bank: fParams?.bank?.trim(),
    };

    if (newPrams.is_active === "all") delete newPrams.is_active;

    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Search Payout Bank list", newPrams);
    setSearched(true);
    setFilter(newPrams);
    queryPage(newPagination, newPrams);
  };

  const handleCloseDialog = () => {
    openCreateDialog && setOpenCreateDialog(null);
  };

  // ------------------ end of filter stuffies --------------------//

  const dataNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const schema = [
    {
      type: "text",
      label: "Account Number",
      param: "account_number",
    },
    {
      type: "text",
      label: "Bank Name",
      param: "bank",
    },
    {
      type: "select",
      label: "Status",
      param: "is_active",
      options: [
        { id: "all", name: "All" },
        { id: true, name: "Active" },
        { id: false, name: "Inactive" },
      ],
    },
  ];

  return (
    <Box>
      {userRole.includes("Add") && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          mb={2}
        >
          <Button
            variant="contained"
            onClick={() => setOpenCreateDialog(true)}
            startIcon={<Add />}
            sx={{ ...styles.customButton }}
          >
            Add Bank Account
          </Button>
        </Stack>
      )}

      <FormSearch
        schema={schema}
        loading={fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {!searched ? (
        <SearchPrompt />
      ) : loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const {
                      account_number,
                      bank,
                      bank_address,
                      bic_swift_code,
                      account_name,
                      iban,
                      is_active,
                    } = row;
                      
                    return (
                      <StyledTableRow hover key={i}>
                        <CustomTableCell field={account_number} />
                        <CustomTableCell field={account_name} />
                        <CustomTableCell field={bank} />
                        <CustomTableCell field={bank_address} />
                        <CustomTableCell field={bic_swift_code} />
                        <CustomTableCell field={iban} />
                        <CustomTableCell
                          field={
                            <Label color={is_active ? "success" : "error"}>
                              {is_active ? "YES" : "NO"}
                            </Label>
                          }
                        />
                        <CustomTableCell
                          align="right"
                          field={
                            <>
                              {userRole.includes("Edit") && (
                                <Tooltip title="Edit" placement="top">
                                  <IconButton
                                    onClick={() => {
                                      const url = `/bank_account/bank_account/details/${row.id}/`;
                                      setGlobalLoading(true);
                                      sendDataWithGet(
                                        url,
                                        setOpenCreateDialog,
                                        () => setGlobalLoading(false)
                                      );
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          }
                        />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {dataNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}

      {openCreateDialog && (
        <CreateBankAccountDialog
          data={{
            details: openCreateDialog,
          }}
          onClose={handleCloseDialog}
          cb={queryPage}
        />
      )}

      {openDeleteDialog && (
        <DeleteBankAccountDialog
          open={openDeleteDialog}
          selectedId={selectedId}
          setResponse={setTableData}
          setLoaded={setLoaded}
          setOpenDeleteDialog={setOpenDeleteDialog}
          setSelectedId={setSelectedId}
        />
      )}
    </Box>
  );
}
